import { useState } from 'react';
import {
    Mail,
    Send,
    Clock,
    Trash2,
    RefreshCw,
    Loader2,
    Copy,
    Check,
    UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useWorkspaceInvites } from '@/hooks/useWorkspaceInvites';
import type { WorkspaceRole } from '@/types/workspace';
import { cn } from '@/lib/utils';

export function WorkspaceInvites() {
    const { isAdmin } = useWorkspace();
    const { invites, loading, error, createInvite, cancelInvite, resendInvite } = useWorkspaceInvites();

    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<WorkspaceRole>('member');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [copiedLink, setCopiedLink] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const handleSendInvite = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!inviteEmail.includes('@')) {
            setSubmitError('Please enter a valid email address');
            return;
        }

        setSubmitting(true);
        setSubmitError(null);

        try {
            const result = await createInvite({ email: inviteEmail, role: inviteRole });
            if (result) {
                setInviteEmail('');
                setInviteRole('member');
                setShowInviteForm(false);

                // Copy invite link to clipboard
                const fullLink = `${window.location.origin}${result.inviteLink}`;
                await navigator.clipboard.writeText(fullLink);
                setCopiedLink(result.invite.id);
                setTimeout(() => setCopiedLink(null), 3000);
            }
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to send invite');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelInvite = async (inviteId: string) => {
        setActionLoading(inviteId);
        try {
            await cancelInvite(inviteId);
        } finally {
            setActionLoading(null);
        }
    };

    const handleResendInvite = async (inviteId: string) => {
        setActionLoading(inviteId);
        try {
            const result = await resendInvite(inviteId);
            if (result) {
                const fullLink = `${window.location.origin}${result.inviteLink}`;
                await navigator.clipboard.writeText(fullLink);
                setCopiedLink(result.invite.id);
                setTimeout(() => setCopiedLink(null), 3000);
            }
        } finally {
            setActionLoading(null);
        }
    };

    const copyInviteLink = async (token: string, inviteId: string) => {
        const fullLink = `${window.location.origin}/invites/${token}/accept`;
        await navigator.clipboard.writeText(fullLink);
        setCopiedLink(inviteId);
        setTimeout(() => setCopiedLink(null), 3000);
    };

    const formatExpiry = (expiresAt: string) => {
        const expiry = new Date(expiresAt);
        const now = new Date();
        const diff = expiry.getTime() - now.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));

        if (hours <= 0) return 'Expired';
        if (hours < 24) return `${hours}h remaining`;
        return `${Math.floor(hours / 24)}d remaining`;
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Mail className="h-5 w-5" />
                                Pending Invites
                            </CardTitle>
                            <CardDescription>
                                Invite team members to join your workspace
                            </CardDescription>
                        </div>
                        <Button onClick={() => setShowInviteForm(true)}>
                            <UserPlus className="h-4 w-4 mr-2" />
                            Invite Member
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="space-y-3">
                            {[1, 2].map((i) => (
                                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                                    <div className="size-10 rounded-full bg-muted animate-pulse" />
                                    <div className="flex-1">
                                        <div className="h-4 w-48 bg-muted rounded animate-pulse mb-2" />
                                        <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
                            {error}
                        </div>
                    ) : invites.length === 0 ? (
                        <div className="text-center py-8">
                            <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">No pending invites</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Invite team members to collaborate in your workspace
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {invites.map((invite: any) => (
                                <div
                                    key={invite.id}
                                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                                >
                                    {/* Avatar */}
                                    <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Mail className="h-4 w-4 text-primary" />
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium truncate">
                                                {invite.email}
                                            </span>
                                            <span className={cn(
                                                "px-2 py-0.5 rounded text-xs font-medium",
                                                invite.role === 'admin'
                                                    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                                    : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                            )}>
                                                {invite.role}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Clock className="h-3 w-3" />
                                            <span>{formatExpiry(invite.expiresAt)}</span>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => copyInviteLink(invite.token, invite.id)}
                                            title="Copy invite link"
                                        >
                                            {copiedLink === invite.id ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => handleResendInvite(invite.id)}
                                            disabled={actionLoading === invite.id}
                                            title="Resend invite"
                                        >
                                            {actionLoading === invite.id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <RefreshCw className="h-4 w-4" />
                                            )}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-destructive hover:text-destructive"
                                            onClick={() => handleCancelInvite(invite.id)}
                                            disabled={actionLoading === invite.id}
                                            title="Cancel invite"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Invite Dialog */}
            <Dialog open={showInviteForm} onOpenChange={setShowInviteForm}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Invite Team Member</DialogTitle>
                        <DialogDescription>
                            Send an invite to add a new member to your workspace
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSendInvite}>
                        <div className="space-y-4 py-4">
                            {submitError && (
                                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                                    {submitError}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="email">Email Address</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="teammate@company.com"
                                    required
                                    disabled={submitting}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Role</Label>
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant={inviteRole === 'member' ? 'default' : 'outline'}
                                        className="flex-1"
                                        onClick={() => setInviteRole('member')}
                                        disabled={submitting}
                                    >
                                        Member
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={inviteRole === 'admin' ? 'default' : 'outline'}
                                        className="flex-1"
                                        onClick={() => setInviteRole('admin')}
                                        disabled={submitting}
                                    >
                                        Admin
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {inviteRole === 'admin'
                                        ? 'Admins can manage members, settings, and billing'
                                        : 'Members can access workspace features but cannot manage settings'}
                                </p>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowInviteForm(false)}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitting || !inviteEmail}>
                                {submitting ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4 mr-2" />
                                )}
                                Send Invite
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
